import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Modal, ModalDialog,
    ModalClose, Avatar, Tooltip, Textarea,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import AddRoundedIcon              from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon           from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon           from '@mui/icons-material/SearchRounded';
import GavelRoundedIcon            from '@mui/icons-material/GavelRounded';
import FilterListRoundedIcon       from '@mui/icons-material/FilterListRounded';
import ArrowBackRoundedIcon        from '@mui/icons-material/ArrowBackRounded';
import BlockRoundedIcon            from '@mui/icons-material/BlockRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import ExpandMoreRoundedIcon       from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon       from '@mui/icons-material/ExpandLessRounded';
import RefreshRoundedIcon          from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon             from '@mui/icons-material/SaveRounded';
import WarningAmberRoundedIcon     from '@mui/icons-material/WarningAmberRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetFines, APICreateFine, APICancelFine, APIDeleteFine,
    APIGetEmployees,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const chipSx = {
    cursor: 'pointer', fontWeight: 700, fontSize: 12,
    transition: 'all .15s', px: 2, py: 0.5,
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    '&:hover': { bgcolor: 'var(--joy-palette-background-level3)', '& .MuiChip-label': { color: 'black' } },
    '& .MuiChip-label': { color: 'white' }
};

// ════════════════════════════════════════════════════════════════════
//  Модалка создания штрафа / списания
// ════════════════════════════════════════════════════════════════════
function NewFineModal({ open, onClose, onSubmit, photographers, processing }) {
    const [photoIds, setPhotoIds] = useState([]);
    const [value,    setValue]    = useState('');
    const [reason,   setReason]   = useState('');
    const [kind,     setKind]     = useState('fine');         // 'fine' | 'deduction'
    const [target,   setTarget]   = useState('photographer'); // 'photographer' | 'administrator'

    useEffect(() => {
        if (open) {
            setPhotoIds([]);
            setValue('');
            setReason('');
            setKind('fine');
            setTarget('photographer');
        }
    }, [open]);

    const handleSubmit = () => {
        if (!photoIds.length || !parseFloat(value) || !reason.trim()) return;
        onSubmit({
            photographer_ids: photoIds,
            value: parseFloat(value),
            reason: reason.trim(),
            is_deduction: kind === 'deduction',
            is_administrator: target === 'administrator',
        });
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-surface)',
                border: '1px solid var(--joy-palette-divider)',
                maxWidth: 460, width: '100%',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 2 }}>
                    Новый штраф / списание
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Сотрудники
                    </Typography>
                    {photoIds.length > 0 && (
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                            Выбрано: {photoIds.length}
                        </Typography>
                    )}
                </Box>
                <Select
                    multiple
                    size="sm" value={photoIds}
                    onChange={(_, v) => setPhotoIds(v || [])}
                    placeholder="Выберите сотрудника(ов)"
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                            {selected.map((opt) => (
                                <Chip key={opt.value} size="sm" sx={{
                                    bgcolor: 'rgba(162,38,226,0.12)', color: 'var(--joy-palette-primary-300)',
                                    fontWeight: 700, fontSize: 11, border: 'none',
                                }}>
                                    {opt.label}
                                </Chip>
                            ))}
                        </Box>
                    )}
                    slotProps={{
                        listbox: {
                            sx: {
                                zIndex: 1400, bgcolor: '#1b1b2e',
                                border: '1px solid var(--joy-palette-divider)',
                                '& .MuiOption-root': {
                                    color: '#eaeaf5',
                                    '&:hover': { bgcolor: 'rgba(162,38,226,0.16)', color: '#fff' },
                                    '&[aria-selected="true"]': { bgcolor: 'rgba(162,38,226,0.22)', color: 'var(--joy-palette-primary-300)' },
                                },
                            },
                        },
                    }}
                    sx={{
                        bgcolor: 'var(--joy-palette-background-level2)',
                        border: '1px solid var(--joy-palette-divider)',
                        fontSize: 13, color: 'white', flex: 1,
                        '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                        "&:hover": { bgcolor: 'var(--joy-palette-background-level3)' },
                    }}
                >
                    {photographers.map(p => (
                        <Option key={p.id} value={p.id}>{p.first_name} {p.last_name}</Option>
                    ))}
                </Select>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                            Тип
                        </Typography>
                        <Select size="sm" value={kind}
                            onChange={(_, v) => setKind(v || 'fine')}
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13, color: 'white', flex: 1,
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                                "&:hover": {
                                    bgcolor: 'var(--joy-palette-background-level3)', 
                                }
                            }}
                        >
                            <Option value="fine">Штраф</Option>
                            <Option value="deduction">Списание</Option>
                        </Select>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                            Кому
                        </Typography>
                        <Select size="sm" value={target}
                            onChange={(_, v) => setTarget(v || 'photographer')}
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13, color: 'white', flex: 1,
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                                "&:hover": {
                                    bgcolor: 'var(--joy-palette-background-level3)', 
                                }
                            }}
                        >
                            <Option value="photographer">Как фотографу</Option>
                            <Option value="administrator">Как админу</Option>
                        </Select>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                    Сумма, ₽
                </Typography>
                <Input
                    type="number" size="sm" placeholder="500"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    sx={{ mb: 2, width: '100%' }}
                />

                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
                    Причина
                </Typography>
                <Textarea
                    minRows={2} maxRows={5}
                    placeholder="Опишите причину штрафа/списания…"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{
                        mb: 2, width: '100%',
                        '& textarea': { fontSize: 13 },
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="soft" color="neutral" size="md" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        size="md" loading={processing}
                        startDecorator={<SaveRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleSubmit}
                        disabled={!photoIds || !parseFloat(value) || !reason.trim()}
                        sx={{
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700,
                        }}
                    >
                        Создать
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  WorkstationFinesPage
// ════════════════════════════════════════════════════════════════════
export default function WorkstationFinesPage() {
    const { token, showToast, onRefreshTick, activeShift } = useContext(AppContext);
    const navigate = useNavigate();

    // ── Данные ─────────────────────────────────────────────────────
    const [fines,       setFines]       = useState([]);
    const [employees,   setEmployees]   = useState([]);
    const [loading,     setLoading]     = useState(true);

    // ── Фильтры ────────────────────────────────────────────────────
    const today = useMemo(() => toISO(new Date()), []);
    const monthStart = useMemo(() => {
        const d = new Date(); return toISO(new Date(d.getFullYear(), d.getMonth(), 1));
    }, []);
    const [dateFrom,    setDateFrom]    = useState(monthStart);
    const [dateTo,      setDateTo]      = useState(today);
    const [filterPhoto, setFilterPhoto] = useState('');
    const [filterKind,  setFilterKind]  = useState('');     // '' | 'fine' | 'deduction'
    const [filterTarget,setFilterTarget]= useState('');     // '' | 'photographer' | 'administrator'
    const [showOnlyActive, setShowOnlyActive] = useState(true);
    const [search,      setSearch]      = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // ── UI стейт ───────────────────────────────────────────────────
    const [showStats,   setShowStats]   = useState(true);

    // ── Модалки ────────────────────────────────────────────────────
    const [newModal,    setNewModal]    = useState(false);
    const [cancelTarget,setCancelTarget]= useState(null);
    const [deleteTarget,setDeleteTarget]= useState(null);
    const [processing,  setProcessing]  = useState(false);

    // ── Загрузка ───────────────────────────────────────────────────
    const loadFines = useCallback(async () => {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
        if (filterPhoto) params.photographer_id = filterPhoto;
        if (filterKind)  params.is_deduction = filterKind === 'deduction';
        if (filterTarget) params.is_administrator = filterTarget === 'administrator';
        if (showOnlyActive) params.is_active = true;

        const data = await APIGetFines(token, params);
        if (Array.isArray(data)) setFines(data);
    }, [token, dateFrom, dateTo, filterPhoto, filterKind, filterTarget, showOnlyActive]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        await loadFines();
        setLoading(false);
        return true;
    }, [loadFines]);

    const loadMeta = useCallback(async () => {
        const empl = await APIGetEmployees(token, 'can_have_orders');
        if (Array.isArray(empl)) setEmployees(empl);
    }, [token]);

    useEffect(() => { loadMeta(); }, [loadMeta]);
    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // ── Действия ───────────────────────────────────────────────────
    const handleCreate = async (payload) => {
        setProcessing(true);
        const res = await APICreateFine(token, {
            ...payload,
            date: toISO(new Date()),
            // location_id, administrator_id — workstation сервер подставит сам
        });
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось создать штраф'), 'danger');
        } else {
            const kindLabel = payload.is_deduction ? 'Списание' : 'Штраф';
            showToast(`${kindLabel} создан · ${fmtMoney(payload.value)} ₽`);
            setNewModal(false);
            loadFines();
        }
        setProcessing(false);
    };

    const handleCancel = async () => {
        if (!cancelTarget) return;
        setProcessing(true);
        const res = await APICancelFine(token, cancelTarget.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
        } else {
            showToast('Штраф отменён');
            loadFines();
        }
        setProcessing(false);
        setCancelTarget(null);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setProcessing(true);
        const res = await APIDeleteFine(token, deleteTarget.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить'), 'danger');
        } else {
            showToast('Штраф удалён');
            setFines(prev => prev.filter(f => f.id !== deleteTarget.id));
        }
        setProcessing(false);
        setDeleteTarget(null);
    };

    // ── Фильтрация на клиенте ──────────────────────────────────────
    const filtered = useMemo(() => {
        let list = fines;
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(f =>
                (f.photographer_name || '').toLowerCase().includes(q) ||
                (f.reason || '').toLowerCase().includes(q) ||
                String(f.value).includes(q)
            );
        }
        return list;
    }, [fines, search]);

    // ── Итоги ──────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const active = filtered.filter(f => f.is_active);
        const fineSum = active.filter(f => !f.is_deduction).reduce((s, f) => s + parseFloat(f.value || 0), 0);
        const deductionSum = active.filter(f => f.is_deduction).reduce((s, f) => s + parseFloat(f.value || 0), 0);
        return {
            count: filtered.length,
            activeCount: active.length,
            fineSum, deductionSum,
            total: fineSum + deductionSum,
        };
    }, [filtered]);

    // ── Группировка по дате ────────────────────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        const sorted = [...filtered].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
        for (const f of sorted) {
            const key = f.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(f);
        }
        return Object.entries(map);
    }, [filtered]);

    // ── Быстрые периоды ────────────────────────────────────────────
    const quickPeriods = useMemo(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 6);
        return [
            { label: 'Сегодня', from: toISO(today),         to: toISO(today) },
            { label: 'Неделя',  from: toISO(weekAgo),       to: toISO(today) },
            { label: 'Месяц',   from: toISO(startOfMonth),  to: toISO(today) },
        ];
    }, []);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ────────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/workstation')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Штрафы и списания
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    {activeShift?.location?.name || activeShift?.location_name || '—'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                variant="outlined" color="primary"
                                onClick={loadAll}
                                sx={{
                                    // bgcolor: 'var(--joy-palette-background-level2)',
                                    // border: '1px solid var(--joy-palette-divider)',
                                }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setNewModal(true)}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Новый штраф
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Тоггл статистики ─────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Статистика за период
                    </Typography>
                    <Button
                        size="sm" variant="plain" color="neutral"
                        endDecorator={showStats
                            ? <ExpandLessRoundedIcon sx={{ fontSize: 16 }} />
                            : <ExpandMoreRoundedIcon sx={{ fontSize: 16 }} />
                        }
                        onClick={() => setShowStats(p => !p)}
                        sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)' }}
                    >
                        {showStats ? 'Скрыть' : 'Показать'}
                    </Button>
                </Box>

                <AnimatePresence initial={false}>
                    {showStats && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5, mb: 2.5 }}>
                                {[
                                    {
                                        label: 'Всего штрафов', value: fmtMoney(totals.fineSum),
                                        sub: `${filtered.filter(f => !f.is_deduction && f.is_active).length} шт.`,
                                        color: '#ef4444', bg: 'rgba(239,68,68,0.08)',
                                        icon: <GavelRoundedIcon sx={{ fontSize: 18 }} />,
                                    },
                                    {
                                        label: 'Списаний', value: fmtMoney(totals.deductionSum),
                                        sub: `${filtered.filter(f => f.is_deduction && f.is_active).length} шт.`,
                                        color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
                                        icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />,
                                    },
                                    {
                                        label: 'Активные', value: totals.activeCount,
                                        color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)',
                                        icon: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />,
                                    },
                                    {
                                        label: 'Всего записей', value: totals.count,
                                        color: '#06b6d4', bg: 'rgba(6,182,212,0.08)',
                                        icon: <FilterListRoundedIcon sx={{ fontSize: 18 }} />,
                                    },
                                ].map(s => (
                                    <Box key={s.label} sx={{
                                        px: 2, py: 1.8,
                                        borderRadius: 'var(--joy-radius-lg)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        bgcolor: s.bg,
                                    }}>
                                        <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                            {s.value}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                            {s.label}
                                        </Typography>
                                        {s.sub && (
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                {s.sub}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Фильтры ──────────────────────────────────────── */}
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 1.5, sm: 2 }, mb: 2.5,
                }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Input
                            placeholder="Поиск по имени, причине, сумме…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />}
                            size="sm"
                        />
                        <IconButton
                            variant={showFilters ? 'solid' : 'outlined'}
                            color={showFilters ? 'primary' : 'neutral'}
                            onClick={() => setShowFilters(p => !p)}
                            size="sm"
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                '&:hover': { bgcolor: 'var(--joy-palette-background-level3)' },
                            }}
                        >
                            <FilterListRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
                        </IconButton>
                    </Box>

                    {/* Быстрые периоды + переключатель "только активные" */}
                    <Box sx={{ display: 'flex', gap: 0.8, mb: showFilters ? 1.5 : 0, flexWrap: 'wrap' }}>
                        {quickPeriods.map(p => {
                            const active = dateFrom === p.from && dateTo === p.to;
                            return (
                                <Chip
                                    key={p.label} size="sm"
                                    variant={active ? 'solid' : 'outlined'} 
                                    color={active ? 'primary' : 'neutral'}
                                    onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                                    sx={chipSx}
                                >
                                    {p.label}
                                </Chip>
                            );
                        })}
                        <Box sx={{ flex: 1 }} />
                        <Chip
                            size="sm"
                            variant={showOnlyActive ? 'solid' : 'outlined'}
                            color={showOnlyActive ? 'primary' : 'neutral'}
                            onClick={() => setShowOnlyActive(p => !p)}
                            sx={{
                                cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5,
                            }}
                        >
                            Только активные
                        </Chip>
                    </Box>

                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                                    <Input type="date" size="sm" value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                    />
                                    <Input type="date" size="sm" value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                    />
                                    <Select size="sm" value={filterPhoto} onChange={(_, v) => setFilterPhoto(v || '')}
                                        placeholder="Сотрудник"
                                    >
                                        <Option value="">Все сотрудники</Option>
                                        {employees.map(e => (
                                            <Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Option>
                                        ))}
                                    </Select>
                                    <Select size="sm" value={filterKind} onChange={(_, v) => setFilterKind(v || '')}
                                        placeholder="Тип"
                                    >
                                        <Option value="">Все типы</Option>
                                        <Option value="fine">Штрафы</Option>
                                        <Option value="deduction">Списания</Option>
                                    </Select>
                                    <Select size="sm" value={filterTarget} onChange={(_, v) => setFilterTarget(v || '')}
                                        placeholder="Кому"
                                        sx={{ gridColumn: { xs: 'span 2', sm: 'span 1' } }}
                                    >
                                        <Option value="">Всем</Option>
                                        <Option value="photographer">Фотографам</Option>
                                        <Option value="administrator">Админам</Option>
                                    </Select>
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>

                {/* ── Список штрафов ────────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <GavelRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            За выбранный период нет штрафов, так держать 👏
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2}>
                            {grouped.map(([date, items]) => (
                                <motion.div key={date} variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        overflow: 'hidden',
                                    }}>
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderBottom: '1px solid var(--joy-palette-divider)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {fmtDate(date)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {items.length} шт. · {fmtMoney(items.filter(f => f.is_active).reduce((s, f) => s + parseFloat(f.value || 0), 0))} ₽
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0}>
                                            {items.map((fine, idx) => {
                                                const isDeduction = fine.is_deduction;
                                                const isAdmin = fine.is_administrator;
                                                const accent = isDeduction ? '#f59e0b' : '#ef4444';
                                                const accentBg = isDeduction ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)';

                                                return (
                                                    <Box key={fine.id} sx={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                                        px: 2, py: 1.3,
                                                        borderBottom: idx < items.length - 1
                                                            ? '1px solid var(--joy-palette-divider)' : 'none',
                                                        opacity: fine.is_active ? 1 : 0.5,
                                                        transition: 'background .15s',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        <Avatar src={fine.photographer_avatar_url} size="sm" sx={{
                                                            width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                            bgcolor: accentBg,
                                                            color: accent,
                                                            border: `1.5px solid ${accent}`,
                                                            flexShrink: 0,
                                                            mt: 0.3,
                                                        }}>
                                                            {(fine.photographer_name || '').charAt(0).toUpperCase()}
                                                        </Avatar>

                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                    {fine.photographer_name}
                                                                </Typography>
                                                                <Chip size="sm" variant="soft" sx={{
                                                                    fontSize: 10, fontWeight: 700,
                                                                    bgcolor: accentBg, color: accent,
                                                                }}>
                                                                    {isDeduction ? 'Списание' : 'Штраф'}
                                                                </Chip>
                                                                {isAdmin && (
                                                                    <Chip size="sm" variant="soft" sx={{
                                                                        fontSize: 10, fontWeight: 700,
                                                                        bgcolor: 'rgba(59,130,246,0.10)', color: '#3b82f6',
                                                                    }}>
                                                                        Как админу
                                                                    </Chip>
                                                                )}
                                                                {!fine.is_active && (
                                                                    <Chip size="sm" variant="soft" sx={{
                                                                        fontSize: 10, fontWeight: 700,
                                                                        bgcolor: 'rgba(150,150,150,0.10)', color: 'var(--joy-palette-text-tertiary)',
                                                                    }}>
                                                                        Отменён
                                                                    </Chip>
                                                                )}
                                                            </Box>
                                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mt: 0.3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {fine.reason}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.4, flexWrap: 'wrap' }}>
                                                                {fine.created_by_name && (
                                                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                        Выписал: {fine.created_by_name}
                                                                    </Typography>
                                                                )}
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>·</Typography>
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {fmtTime(fine.date_create)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Typography sx={{
                                                            fontSize: 16, fontWeight: 800,
                                                            color: accent,
                                                            minWidth: 70, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            mt: 0.3,
                                                            flexShrink: 0,
                                                        }}>
                                                            −{fmtMoney(fine.value)}
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flexShrink: 0 }}>
                                                            {fine.is_active && (
                                                                <Tooltip title="Отменить (soft-delete)" size="sm">
                                                                    <IconButton
                                                                        size="sm" variant="plain"
                                                                        onClick={() => setCancelTarget(fine)}
                                                                        sx={{
                                                                            color: 'var(--joy-palette-text-tertiary)',
                                                                            '&:hover': { color: '#f59e0b' },
                                                                        }}
                                                                    >
                                                                        <BlockRoundedIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title="Удалить навсегда" size="sm">
                                                                <IconButton
                                                                    size="sm" variant="plain"
                                                                    onClick={() => setDeleteTarget(fine)}
                                                                    sx={{
                                                                        color: 'var(--joy-palette-text-tertiary)',
                                                                        '&:hover': { color: 'var(--joy-palette-danger-400)' },
                                                                    }}
                                                                >
                                                                    <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            <NewFineModal
                open={newModal}
                onClose={() => setNewModal(false)}
                onSubmit={handleCreate}
                photographers={employees}
                processing={processing}
            />

            {/* ── Модалка отмены (soft) ─────────────────────────── */}
            <Modal open={!!cancelTarget} onClose={() => setCancelTarget(null)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Отменить штраф?
                    </Typography>
                    {cancelTarget && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                            {cancelTarget.photographer_name} — {fmtMoney(cancelTarget.value)} ₽ ({cancelTarget.is_deduction ? 'списание' : 'штраф'})
                            <br />
                            <span style={{ color: 'var(--joy-palette-text-tertiary)' }}>{cancelTarget.reason}</span>
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm"
                            onClick={() => setCancelTarget(null)}>
                            Назад
                        </Button>
                        <Button color="warning" size="sm" loading={processing} onClick={handleCancel}>
                            Отменить штраф
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* ── Модалка удаления (hard) ───────────────────────── */}
            <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить запись навсегда?
                    </Typography>
                    {deleteTarget && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                            {deleteTarget.photographer_name} — {fmtMoney(deleteTarget.value)} ₽
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm"
                            onClick={() => setDeleteTarget(null)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={processing} onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}